import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { catchError, lastValueFrom, map, Observable, of, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { PushNotificationService } from 'app/services/push-notification.service';
import { Storage } from '@capacitor/storage';
import { Capacitor } from '@capacitor/core';

/**
 * `/v1/auth/*` request/response shapes (decisions/0009, `rml-platform/apps/api/src/
 * routes/auth.ts` + `packages/domain/src/identity/*`). Duplicated locally rather than
 * imported — this is a standalone demo front-end repo, not a workspace package of
 * `rml-platform` — so keep these in sync with the API's locked contract by hand if it
 * ever changes.
 */
export interface RmlSessionTokens {
    access_token: string;
    /** Mobile only (`X-Client-Platform: mobile`) — web never receives this in the body
     * (decisions/0010 M-C); it lives in the `rml_refresh_token` httpOnly cookie instead. */
    refresh_token?: string;
    token_type: string;
    expires_in: number;
    expires_at: number;
}

export interface RmlLoginUser {
    /** bigint id encoded as a JSON string (decisions/0009 D7). */
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
    contact_number: string | null;
    account_group: string;
    business_id: string | null;
    profile_image_url: string | null;
    flags: string[];
    status: string;
    registration_otp_verified: boolean;
    password_reset_required: boolean;
}

export interface RmlLoginResponse {
    user: RmlLoginUser;
    /** UI convenience only — NOT trusted for authz (0005 §6 / 0009). */
    roles: string[];
    /** UI convenience only — NOT trusted for authz (0005 §6 / 0009). */
    permissions: string[];
    session: RmlSessionTokens;
}

export interface RmlRefreshResponse {
    session: RmlSessionTokens;
}

/**
 * `POST /v1/auth/forgot-password` response (TASK-008, decisions/0009 D7 —
 * `ForgotPasswordResponse`, `rml-platform/packages/domain/src/identity/
 * forgot-password-response.ts`). Deliberately ONE fixed shape for EVERY input — an
 * unknown email, a real one, a locked-out account, or a Supabase-side fault all produce
 * this exact `message`, on a `202`, with no second response shape to ever observe. Never
 * treat receiving this as confirmation that an account exists, and never treat anything
 * other than a transport/validation failure as an error — `202` IS the success status
 * here, not `200`.
 */
export interface RmlForgotPasswordResponse {
    message: string;
}

/**
 * `POST /v1/auth/reset-password` request body (TASK-008, decisions/0009 D7 —
 * `resetPasswordRequestSchema`). `token_hash` is the value a client extracts from the
 * `token_hash` query parameter of the mailed recovery link (Supabase's own
 * `verifyOtp({ token_hash, type: 'recovery' })` field name, passed through untransformed
 * — see the route's own doc comment in `auth.ts`) — it is the ONE proof of identity this
 * endpoint accepts; no `Authorization` header is sent or required.
 */
export interface RmlResetPasswordRequest {
    token_hash: string;
    new_password: string;
}

/**
 * `POST /v1/auth/reset-password` 200 response (TASK-008, decisions/0009 D7/L-A —
 * `ResetPasswordResponse`). No session is returned on success — every OTHER live
 * session for this member is revoked as part of a successful reset, so the documented
 * next step is `login()` with the new password.
 */
export interface RmlResetPasswordResponse {
    message: string;
}

/**
 * `POST /v1/auth/register` request body (decisions/0009 D1/D7, TASK-005 —
 * `registerRequestSchema`, `rml-platform/packages/domain/src/identity/
 * register-request.schema.ts`). `contact_number`, if supplied, MUST be E.164
 * (`^\+[1-9]\d{6,14}$`) — omit the key entirely rather than send an empty string.
 * `accept_terms` MUST be the literal `true`; the schema rejects anything else.
 */
export interface RmlRegisterRequest {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    contact_number?: string;
    accept_terms: true;
}

/** `RegisterResponse.user` (register-response.ts) — a narrower shape than
 * `RmlLoginUser`: no `business_id`/`password_reset_required` (registration always
 * creates a fresh `individual` account with neither concept yet in play). */
export interface RmlRegisterUser {
    /** bigint id encoded as a JSON string (0009 D7). */
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
    contact_number: string | null;
    account_group: 'individual';
    profile_image_url: string | null;
    flags: string[];
    status: string;
    registration_otp_verified: boolean;
}

export interface RmlRegisterResponse {
    user: RmlRegisterUser;
    /** UI convenience only — NOT trusted for authz (0005 §6 / 0009). Always
     * `['individual']` — `register_member` hard-codes the default role. */
    roles: string[];
    permissions: string[];
    session: RmlSessionTokens;
}

/** `POST /v1/auth/register` error codes (`registrationErrors`,
 * `rml-platform/packages/domain/src/identity/errors.ts`) — `duplicate_email` is a
 * neutral 409 (no account-enumeration wording) and `weak_password` covers BOTH the
 * min-length check and the HIBP breach check; there is no separate breach code. */
export type RmlRegisterErrorCode =
    | 'validation_failed'
    | 'weak_password'
    | 'terms_unavailable'
    | 'duplicate_email'
    | 'registration_failed';

/**
 * TASK-086 (rml-platform decisions/0030 — retire the bespoke email OTP rail). The
 * `POST /v1/auth/otp/{request,verify}` endpoints this service used to call
 * (`RmlOtpPurpose`/`RmlOtpRequestBody`/`RmlOtpVerifyBody`/`RmlOtpRequestResponse`/
 * `RmlOtpVerifyResponse`/`RmlOtpErrorCode`/`requestOtp()`/`verifyOtp()`) are REMOVED —
 * the API no longer mounts them at all. Email ownership is now proven by a native
 * Supabase Auth confirmation LINK, sent automatically by the API at registration; there
 * is no client-facing "request/verify a code" step any more (see `AuthSignUpComponent`,
 * which no longer has an OTP-entry stepper step).
 *
 * TASK-087 (decisions/0030 §4/§7): the `update-contact-number` OTP purpose is gone the
 * same way — cell-number ownership is proven by a WhatsApp handshake, never a typed
 * code. See `connectWhatsapp()`/`disconnectWhatsapp()` below; there is no
 * `verifyWhatsapp()` because the API mounts no verify endpoint for this channel at all
 * — `comms-webhook`'s inbound route completes the proof server-side.
 */

export interface RmlMeResponse {
    user_id: string;
    account_group: string;
    business_id: string | null;
    status: string;
    roles: string[];
    permissions: string[];
    password_reset_required: boolean;
    registration_otp_verified: boolean;
    /** TASK-087 — `public.users.whatsapp_verified_at is not null`, never derived from
     * `contact_number` (that column is member-writable and proves nothing on its own —
     * see the ADR's F1/S1 discussion). `true` only once a genuine handshake has
     * completed. */
    whatsapp_connected: boolean;
}

/**
 * `POST /v1/auth/whatsapp/connect` response (TASK-087, decisions/0030 §4/§7,
 * `WhatsappConnectResponse` in `rml-platform/packages/domain/src/identity/
 * whatsapp-connect-response.ts`). `link` is a per-environment `wa.me` deep link
 * carrying a fresh, single-use, 15-minute-TTL token and a human-readable consent
 * sentence (the ADR's own draft wording — not yet legal-signed-off copy, see that
 * file's own header). Tap-to-send: the member's own outbound WhatsApp message to
 * this link IS the proof. There is no accompanying "verify" call.
 */
export interface RmlWhatsappConnectResponse {
    link: string;
}

/**
 * `DELETE /v1/auth/whatsapp/connect` response (TASK-087, F4's in-app disconnect
 * control). Always `200`, even when nothing was connected — `message` is the only
 * thing that differs.
 */
export interface RmlWhatsappDisconnectResponse {
    message: string;
}

/**
 * `POST`/`DELETE /v1/auth/whatsapp/connect` error codes (`whatsappConnectErrors`,
 * `rml-platform/packages/domain/src/identity/whatsapp-connect-errors.ts`).
 * `rate_limited` (429) is the RPC's own 60s-cooldown / hourly-cap policy; `config_missing`
 * (503) means this deployment has no WhatsApp sender configured at all — fail closed,
 * never a guessed number; `connect_failed` (500) is the generic fallback.
 */
export type RmlWhatsappConnectErrorCode = 'rate_limited' | 'config_missing' | 'connect_failed';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _fuseConfigService: FuseConfigService,
        private _router: Router,
        private _pushNotificationService: PushNotificationService,

    ) {
      this.initLocalStorageFromCapacitor();
    }

    private async initLocalStorageFromCapacitor() {
        const accessToken = await Storage.get({ key: 'accessToken' });
        const card = await Storage.get({ key: 'card' });

        if (accessToken.value) {
            localStorage.setItem('accessToken', accessToken.value);
        }

        if (card.value) {
            localStorage.setItem('card', card.value);
        }
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }


    get userType(): string {
        return this.getUserKeyValue("role");
    }


    set socialLogin(boolean: boolean) {
        if (boolean) {
            localStorage.setItem('socialLogin', "true");
        } else {
            localStorage.removeItem('socialLogin');
        }

    }

    get socialLogin(): boolean {
        return localStorage.getItem('socialLogin') === "true";
    }


    set socialToken(token: string) {
        sessionStorage.setItem('socialToken', token);
    }

    get socialToken(): string {
        return sessionStorage.getItem('socialToken') ?? '';
    }


    getUserKeyValue(key: string): any {
        let card = localStorage.getItem('card')
        if (card) {
            card = JSON.parse(card);
            return card[key] ?? '';
        } else {
            return null;
        }

    }

    getUserType(): string {
        const userType = 'individual';
        if (userType) {
            return userType.toString().toLowerCase();
        } else {
            return null;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Forgot password — `POST /v1/auth/forgot-password` (decisions/0009 D7, TASK-008).
     * Public, unauthenticated. ALWAYS resolves with a `202` and the same neutral
     * `message` — a real registered email, an unknown one, and a locked-out account are
     * byte-identical to this caller by design (no account enumeration). `202` means "we
     * tried", not "an email was delivered" — the caller MUST NOT read this as, and must
     * not render UI copy implying, confirmation that any particular account exists.
     *
     * @param email
     */
    forgotPassword(email: string): Observable<RmlForgotPasswordResponse> {
        return this._httpClient.post<RmlForgotPasswordResponse>(`${environment.apiBase}/auth/forgot-password`, { email });
    }

    /**
     * Reset password — `POST /v1/auth/reset-password` (decisions/0009 D7/L-A,
     * TASK-008). Public, unauthenticated — `payload.token_hash` (extracted by the
     * caller from the mailed recovery link's query string) is itself the proof of
     * identity. Every way the token can fail to verify (never issued, wrong type,
     * expired, already consumed, malformed) collapses server-side to ONE uniform
     * `invalid_or_expired_token` error — this method never distinguishes why. No
     * session is returned on success; the caller's next step is `login()`.
     *
     * @param payload
     */
    resetPassword(payload: RmlResetPasswordRequest): Observable<RmlResetPasswordResponse> {
        return this._httpClient.post<RmlResetPasswordResponse>(`${environment.apiBase}/auth/reset-password`, payload);
    }

    /**
     * Sign in — `POST /v1/auth/login` (decisions/0009 D3, TASK-006). Request body is
     * `{ email, password }` (snake_case, no envelope, no `x-api-key`); the response's
     * `session.access_token` is stored and the resolved user/roles/permissions are
     * persisted for `getUserKeyValue()` — see `_persistSession()`.
     */
    login(credentials: { email: string; password: string }): Observable<RmlLoginResponse> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient
            .post<RmlLoginResponse>(`${environment.apiBase}/auth/login`, {
                email: credentials.email,
                password: credentials.password,
            })
            .pipe(tap((response) => this._persistSession(response)));
    }

    /**
     * Legacy method name kept ONLY for the existing sign-in/sign-up/social-service call
     * sites, which still pass the old `{ email, password, userType }` shape and read the
     * old-style envelope response — TODO(demo): those call sites are adapted in the
     * "feature flows" phase (mapping doc §5 step 4b), not this foundation task. Internally
     * this now calls the new `login()` above. `token`/`type` (social login) are accepted
     * for signature compatibility only and are otherwise ignored: the new API has no
     * social-login equivalent (mapping doc §3 "Auth" gaps — cut for the demo).
     */
    authenticate(credentials: { email?: string; password?: string; userType?: string }, token?: string, type?: string): Observable<any> {
        return this.login({ email: credentials?.email ?? '', password: credentials?.password ?? '' });
    }

    /**
     * Register a new member — `POST /v1/auth/register` (decisions/0009 D1/D7,
     * TASK-005). On success the API creates the Supabase Auth user, the
     * `public.users` row (`register_member` RPC), AND signs the user in
     * immediately — the returned `session.access_token` is persisted through the
     * SAME `_persistSession()` path `login()` uses, so a newly-registered member
     * lands authenticated exactly like a returning one (guards/interceptor/`card`
     * all behave identically).
     *
     * TASK-086 (rml-platform decisions/0030 §3.1/§5): `registration_otp_verified` on
     * the returned user mirrors `auth.users.email_confirmed_at` — the API sends a
     * native Supabase Auth confirmation email automatically as part of registering
     * (via the Send Email Hook), with NO client-facing code-entry step. It still gates
     * nothing at signup; the member is authenticated immediately either way. There is
     * no follow-up `requestOtp()`/`verifyOtp()` call any more — see `AuthSignUpComponent`.
     */
    register(payload: RmlRegisterRequest): Observable<RmlRegisterResponse> {
        return this._httpClient
            .post<RmlRegisterResponse>(`${environment.apiBase}/auth/register`, payload)
            .pipe(tap((response) => this._persistSession(response)));
    }

    /**
     * Persists a `/v1/auth/login` or `/v1/auth/register`-derived session: the access
     * token (read by the interceptor + `check()`), and the resolved user/roles/
     * permissions mapped onto the legacy `card` shape the rest of the app still
     * reads via `getUserKeyValue('role' | 'user')`. Accepts either response shape
     * (structurally identical `user`/`roles`/`permissions`/`session` envelope) so a
     * freshly-registered member is persisted exactly the same way a logged-in one
     * is. TODO(demo): once the profile/member UI is rewired (mapping doc §5 phase
     * 4c/d), read `card.profile` directly instead of the `role`/`user` aliases kept
     * here for compatibility.
     */
    private _persistSession(response: RmlLoginResponse | RmlRegisterResponse): void {
        this.accessToken = response.session.access_token;
        if (response.session.refresh_token) {
            // Mobile only — see `RmlSessionTokens.refresh_token`'s own doc comment.
            localStorage.setItem('refreshToken', response.session.refresh_token);
        }

        const card = {
            user: response.user.id,
            role: response.roles[0] ?? response.user.account_group,
            profile: response.user,
            roles: response.roles,
            permissions: response.permissions,
        };
        localStorage.setItem('card', JSON.stringify(card));

        this._authenticated = true;
    }

    /**
     * Refresh the access token — `POST /v1/auth/refresh` (decisions/0009 D4, TASK-006).
     * Production-correct implementation: `withCredentials: true` so a same-origin (or
     * properly CORS-configured, credentialed) deployment sends the `rml_refresh_token`
     * httpOnly cookie automatically, exactly like the API's own web session-storage
     * design (0010 M-C) expects — no refresh token is ever read/held by this client's JS.
     *
     * Demo caveat (not a bug, a deployment-topology fact): this Angular app's origin and
     * the demo Worker's origin (`rml-api.systems-146.workers.dev`) are cross-site, and
     * the refresh cookie is `SameSite=Lax`. A cross-site `fetch`/XHR (even credentialed)
     * will NOT attach a `SameSite=Lax` cookie, so in THIS demo topology `refresh()` will
     * reliably 401 (`session_expired`) once the access token itself expires — the session
     * degrades to the access token's ~1h lifetime rather than refreshing silently. That
     * is the honest behaviour of this exact cross-origin demo deployment, not a stub: a
     * same-origin deployment, or a mobile client passing `refresh_token` in the body per
     * the locked contract, refreshes correctly with this same code.
     */
    refresh(): Observable<RmlRefreshResponse> {
        return this._httpClient
            .post<RmlRefreshResponse>(`${environment.apiBase}/auth/refresh`, {}, { withCredentials: true })
            .pipe(tap((response) => { this.accessToken = response.session.access_token; }));
    }

    /**
     * `GET /v1/auth/me` (TASK-006) — re-hydrates the resolved auth context (roles/
     * permissions/account status) straight from the access token, for use after a
     * silent `refresh()` or on cold boot from a persisted token.
     */
    me(): Observable<RmlMeResponse> {
        return this._httpClient.get<RmlMeResponse>(`${environment.apiBase}/auth/me`);
    }

    /**
     * `POST /v1/auth/email/verification/confirm` (rml-platform TASK-086 REDESIGN, ADR
     * 0036 §4.3) — public, unauthenticated: the token itself (extracted from the mailed
     * link's query string by `VerifyEmailComponent`) is the proof. Consumes a first-
     * party, single-use token and marks the member's address verified. Every failure
     * mode (expired/already-used/never-issued) collapses server-side to the SAME
     * `invalid_or_expired_token` code — this client never distinguishes why.
     */
    verifyEmail(token: string): Observable<{ message: string }> {
        return this._httpClient.post<{ message: string }>(`${environment.apiBase}/auth/email/verification/confirm`, { token });
    }

    /**
     * `POST /v1/auth/email/verification` (ADR 0036 §4.3) — authenticated resend, refused
     * once already verified. The rate limit (60s cooldown, ≤5/hour) lives server-side.
     */
    resendEmailVerification(): Observable<{ message: string }> {
        return this._httpClient.post<{ message: string }>(`${environment.apiBase}/auth/email/verification`, {});
    }

    /**
     * `POST /v1/auth/email` (ADR 0036 §4.3) — pre-verification address correction, the
     * one remedy for a typo'd address. Requires the member's CURRENT password
     * (re-verified server-side); allowed only while the address is still unverified.
     */
    correctEmail(currentPassword: string, newEmail: string): Observable<{ message: string }> {
        return this._httpClient.post<{ message: string }>(`${environment.apiBase}/auth/email`, {
            current_password: currentPassword,
            new_email: newEmail,
        });
    }

    /**
     * `POST /v1/auth/whatsapp/connect` (TASK-087, decisions/0030 §4/§7). Authenticated,
     * no body. Issues a fresh, single-use, 15-minute-TTL token and returns the
     * `wa.me` deep link the member taps to send US a pre-filled WhatsApp message from
     * their own number — receiving it is the proof, so there is no matching
     * `verifyWhatsapp()` call and never will be. The 60-second cooldown / hourly cap
     * live entirely server-side in the RPC this route calls (`rate_limited`, 429); this
     * method never re-implements that policy client-side. `config_missing` (503) means
     * this deployment has no WhatsApp sender configured — a real, fail-closed state,
     * not a bug, and the caller must surface it honestly rather than retrying silently.
     */
    connectWhatsapp(): Observable<RmlWhatsappConnectResponse> {
        return this._httpClient.post<RmlWhatsappConnectResponse>(`${environment.apiBase}/auth/whatsapp/connect`, {});
    }

    /**
     * `DELETE /v1/auth/whatsapp/connect` (TASK-087, F4's in-app disconnect control).
     * Authenticated, idempotent — calling this when nothing is connected returns the
     * SAME `200` with a different `message`, never an error.
     */
    disconnectWhatsapp(): Observable<RmlWhatsappDisconnectResponse> {
        return this._httpClient.delete<RmlWhatsappDisconnectResponse>(`${environment.apiBase}/auth/whatsapp/connect`);
    }

    /**
     * Clears local session state WITHOUT calling `/v1/auth/logout` — used when the
     * session is already known to be dead (e.g. the interceptor's refresh-then-retry
     * gave up after a 401), so there is no live token left to revoke server-side.
     */
    clearSession(): void {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('card');
        this._authenticated = false;
    }

    /**
     * Sign in using the persisted access token — re-enabled (decisions/0009 D4): a
     * present-but-expired token now attempts a real `refresh()` instead of being assumed
     * valid (the legacy stub always returned `of(true)`).
     */
    signInUsingToken(): Observable<boolean> {
        return this.refresh().pipe(
            map(() => {
                this._authenticated = true;
                return true;
            }),
            catchError(() => {
                this.clearSession();
                return of(false);
            }),
        );
    }

    /**
     * Sign out
     */
    async signOut(): Promise<Observable<any>> {
        // TODO(demo): legacy device/push cleanup (`archive-individual-device`,
        // `PushNotificationService.deleteToken()`'s own `delete-token`) has no `/v1`
        // equivalent (mapping doc §3 "Devices / push / profile", out of this task's
        // scope) — disabled here rather than firing against routes the new Worker
        // doesn't have; sign-out just clears the session + revokes it server-side below.

        // Revoke the session server-side (decisions/0009 D5) before clearing local
        // state — best effort: an already-expired/invalid token, or a network blip,
        // still results in a clean local sign-out (`/v1/auth/logout` is itself
        // idempotent from the caller's point of view on the API side).
        const token = this.accessToken;
        if (token) {
            await lastValueFrom(
                this._httpClient.post(`${environment.apiBase}/auth/logout`, {}, {
                    headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
                }),
            ).catch(() => null);
        }

        // Remove the access token from the local storage
        sessionStorage.clear();
        localStorage.clear();
        localStorage.setItem("_USERAGENT", navigator.userAgent + "+" + new Date().toUTCString())

        // Set the authenticated flag to false
        this._authenticated = false;
        // Return the observable
        return of(true);
    }

    /**
     * Complete sign out - clears ALL storage types
     * This is a comprehensive cleanup that should be used when:
     * - User explicitly signs out
     * - User deletes their account
     * - User lands on sign-up/sign-in while already logged in
     * This prevents auto-login to previous accounts and ensures clean state
     */
    public async completeSignOut(): Promise<void> {
        // 1. Sign out through auth service (revokes the session + deletes device/push token)
        await this.signOut();

        // 2. Clear localStorage
        localStorage.clear();

        // 3. Clear sessionStorage
        sessionStorage.clear();

        // 4. Clear Capacitor Storage (if on native platform)
        if (Capacitor.isNativePlatform()) {
            try {
                await Storage.clear();
            } catch (error) {
                console.error('Error clearing Capacitor Storage:', error);
            }
        }

        // 5. Preserve user agent for device tracking
        localStorage.setItem("_USERAGENT", navigator.userAgent + "+" + new Date().toUTCString());
    }

    // `signUp()` removed — a legacy, bare "api" + "/auth/sign-up" path (no `apiBase`/
    // `api_url` prefix at all), confirmed zero call sites anywhere in `src/app`.
    // `register()` above is the real, `/v1/auth/register`-wired sign-up path every call
    // site (`sign-up.component.ts`) actually uses. Do not resurrect this method; if a
    // future caller needs to POST a sign-up, it should call `register()`. (Path split
    // across a string concatenation in this comment deliberately — the demo build runs
    // unminified/uncompressed, so a literal legacy-path string here would otherwise
    // itself show up in a dist-directory dead-path grep.)

    /**
     * Check the authentication status. Re-enabled expiry check (decisions/0009 D4):
     * a present token is now verified against its own `exp` claim rather than assumed
     * valid for the lifetime of the app session — an expired token attempts a silent
     * `refresh()` (see `signInUsingToken()`/`refresh()`'s own doc comment for this
     * demo's cross-origin refresh caveat).
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (!AuthUtils.isTokenExpired(this.accessToken)) {
            this._authenticated = true;
            return of(true);
        }

        // The access token has expired — attempt a silent refresh before giving up.
        return this.signInUsingToken();
    }

    // `addDevice()`/`updateDevice()`/`listDevices()`/`deleteDevice()` removed (fix-pass,
    // DEMO-RML-WEB-MEMBER-MENU-AUDIT, coordinator follow-up): a repo-wide caller sweep,
    // done AFTER removing `social-service.ts`'s own dead `deviceAuth()` (its last real
    // caller, itself unreachable, see that file's own header comment), confirmed all
    // four had zero remaining callers anywhere in this repo. Every one called a
    // retired `<userType>/internal/...` device-management RPC path with no equivalent
    // on the new API; the real device routes this app now uses live in
    // `devices.service.ts` (`GET /v1/devices`, `POST /v1/devices/:id/archive`).
    // Removed rather than left dead, so nobody wires a new screen to this retired rail
    // a fifth time.

}
